import React, { createContext, useContext, useEffect, useState } from 'react';
import { createClient } from '@supabase/supabase-js';
import { useNavigate } from 'react-router-dom';
const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabasePublishableKey = process.env.REACT_APP_SUPABASE_PUBLISHABLE_KEY;
export const supabase = createClient(supabaseUrl, supabasePublishableKey);

const AuthContext = createContext({});

// SessionStorage key for pending auth credentials (email + phone) that survive a page refresh between OTP send and verify. Cleared on sign-out or successful login.
const PENDING_AUTH_STORAGE_KEY = 'mr_pending_auth';

const readPendingAuth = () => {
  try {
    const raw = sessionStorage.getItem(PENDING_AUTH_STORAGE_KEY);
    if (!raw) return { email: null, phone: null };
    const parsed = JSON.parse(raw);
    return {
      email: parsed?.email || null,
      phone: parsed?.phone || null,
    };
  } catch {
    return { email: null, phone: null };
  }
};

const writePendingAuth = (email, phone) => {
  try {
    sessionStorage.setItem(
      PENDING_AUTH_STORAGE_KEY,
      JSON.stringify({ email, phone, savedAt: Date.now() })
    );
  } catch (err) {
    console.warn('[Auth] Failed to persist pending auth credentials', err);
  }
};

const clearPendingAuth = () => {
  try {
    sessionStorage.removeItem(PENDING_AUTH_STORAGE_KEY);
  } catch {
    // ignore
  }
};

export const AuthProvider = ({ children }) => {
  const pendingOnBoot = readPendingAuth();
  const [user, setUser] = useState(null);
  const [userRole, setUserRole] = useState(null); // Start with null instead of default
  const [authLoading, setAuthLoading] = useState(true);
  const [roleLoading, setRoleLoading] = useState(true);
  const [userEmail, setUserEmail] = useState(pendingOnBoot.email);
  const [userPhone, setUserPhone] = useState(pendingOnBoot.phone);
  const [authError, setAuthError] = useState(null);
  const [userName, setUserName] = useState(null);
  const navigate = useNavigate();

  // Turns AU phone formats (0…, 61…, 610…) into E.164 (+61…).
  const formatPhoneNumber = (phone) => {
    if (!phone) return null;
    
    // Remove all non-digit characters
    let digitsOnly = phone.replace(/\D/g, '');
    
    // Handle different Australian phone formats
    if (digitsOnly.startsWith('610')) {
      // 610480123456 -> +61480123456 (remove the extra 0)
      return `+61${digitsOnly.substring(3)}`;
    } else if (digitsOnly.startsWith('61')) {
      // 61480123456 -> +61480123456
      return `+${digitsOnly}`;
    } else if (digitsOnly.startsWith('0')) {
      // 0480123456 -> +61480123456
      return `+61${digitsOnly.substring(1)}`;
    } else {
      // 480123456 -> +61480123456
      return `+61${digitsOnly}`;
    }
  };

  const getPostLoginPath = (role) => {
    return role === 'admin' || role === 'owner' ? '/admin' : '/dashboard';
  };

  // After login, redirect to the stored redirect URL if it exists, otherwise go to the default post-login path based on role.
  const makeRedirect = (role) => {
    const redirectUrl = localStorage.getItem('redirectUrl');
    localStorage.removeItem('redirectUrl');
    const postLoginPath = getPostLoginPath(role);
    
    if (redirectUrl) {
      navigate(redirectUrl);
    } else {
      navigate(postLoginPath);
    }
  };

  // Send OTP to phone number
  const sendOtp = async (email, phone) => {
    setAuthError(null);

    if (!email || !phone) {
      setAuthError("Both email and phone number are required");
      return { success: false, error: "Both email and phone number are required" };
    }

    // Normalize email to lowercase
    const normalizedEmail = email.toLowerCase().trim();
    const formattedPhone = formatPhoneNumber(phone);

    if (!normalizedEmail || !formattedPhone) {
      setAuthError("Both email and phone number are required");
      return { success: false, error: "Both email and phone number are required" };
    }

    try {
      // Check the email/phone combo via edge function (bypasses RLS)
      const credCheckRes = await fetch(
        `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/check-cred-combo`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: normalizedEmail, phone: formattedPhone.substring(1) })
        }
      );
      const credCheck = await credCheckRes.json();

      if (credCheck.conflict === 'email') {
        setAuthError("Email already used with a different phone number");
        return { success: false, error: "Email already used with a different phone number" };
      }

      if (credCheck.conflict === 'phone') {
        setAuthError("Phone number already used with a different email");
        return { success: false, error: "Phone number already used with a different email" };
      }

      if (credCheck.error) {
        setAuthError(credCheck.error);
        return { success: false, error: credCheck.error };
      }

      // Use Supabase Auth to sign in with OTP
      const { error } = await supabase.auth.signInWithOtp({
        phone: formattedPhone
      });

      if (error) {
        setAuthError(error.message);
        return { success: false, error: error.message };
      }

      // Persist across refresh / remount — React state alone is not enough
      // (root cause of null-email users rows after OTP + page reload)
      writePendingAuth(normalizedEmail, formattedPhone);
      setUserEmail(normalizedEmail);
      setUserPhone(formattedPhone);
      
      return { success: true };
    } catch (error) {
      setAuthError(error.message);
      return { success: false, error: error.message };
    }
  };

  // Verify OTP and complete sign in
  const verifyOtp = async (otp) => {
    setAuthError(null);

    const pending = readPendingAuth();
    const emailForVerify = userEmail || pending.email;
    const phoneForVerify = userPhone || pending.phone;

    if (!phoneForVerify || !emailForVerify || !otp) {
      setAuthError("Missing required information");
      return { success: false, error: "Missing required information" };
    }

    // Keep state + storage aligned before auth state change fires get-or-create-user
    writePendingAuth(emailForVerify, phoneForVerify);
    setUserEmail(emailForVerify);
    setUserPhone(phoneForVerify);

    try {
      // First verify the OTP
      const { error } = await supabase.auth.verifyOtp({
        phone: phoneForVerify,
        token: otp,
        type: 'sms'
      });

      if (error) {
        setAuthError(error.message);
        return { success: false, error: error.message };
      }

      // After OTP verification, confirm the email matches via id-based query (RLS allows own row)
      const { data: { user: authUser } } = await supabase.auth.getUser();
      if (authUser) {
        const { data: userData } = await supabase
          .from('users')
          .select('email, name')
          .eq('id', authUser.id)
          .maybeSingle();

        // Null-safe: legacy rows with email=null must not crash login
        const existingEmail = userData?.email?.toLowerCase?.() || null;
        if (existingEmail && existingEmail !== emailForVerify.toLowerCase()) {
          await supabase.auth.signOut();
          clearPendingAuth();
          setAuthError("Invalid email and phone number combination");
          return { success: false, error: "Invalid email and phone number combination" };
        }

        setUserName(userData?.name || userData?.email || emailForVerify || "Unknown");
      }

      return { success: true };
    } catch (error) {
      setAuthError(error.message);
      return { success: false, error: error.message };
    }
  };

  const ensureUserAndFetchRole = async () => {
    if (user) {
      setRoleLoading(true);
      try {
        // Resolve phone from auth user object
        let phone = user.phone;
        if (!phone && user.identities && user.identities.length > 0) {
          const identity = user.identities.find(id => id.provider === 'phone');
          if (identity && identity.identity_data) {
            phone = identity.identity_data.phone;
          }
        }
        // Strip leading '+' to match storage format
        const storedPhone = phone?.startsWith('+') ? phone.substring(1) : phone;

        // Prefer React state, then sessionStorage pending credentials
        // (survives refresh between OTP verify and users-row creation)
        const pending = readPendingAuth();
        const emailForCreate = userEmail || pending.email || null;
        const phoneForCreate = storedPhone || (pending.phone
          ? (pending.phone.startsWith('+') ? pending.phone.substring(1) : pending.phone)
          : null);

        console.log('[Auth] get-or-create-user request', {
          user_id: user.id,
          has_email: !!emailForCreate,
          has_phone: !!phoneForCreate,
          email_from_state: !!userEmail,
          email_from_pending: !!pending.email,
        });

        // Use service-role edge function — bypasses RLS for both SELECT and INSERT
        const res = await fetch(
          `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/get-or-create-user`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              user_id: user.id,
              phone: phoneForCreate,
              email: emailForCreate,
            })
          }
        );
        const result = await res.json();

        if (result.error) {
          console.error('Error in get-or-create-user:', result.error, result.code);
          // Do not leave a half-logged-in first-time user with no email row
          if (result.code === 'EMAIL_REQUIRED') {
            setAuthError(
              'Your session was restored without an email. Please sign in again and enter your email and phone number.'
            );
            clearPendingAuth();
            await supabase.auth.signOut();
            setUserRole(null);
            return;
          }
          setUserRole('customer');
        } else {
          const data = result.user;
          setUserRole(data?.role || 'customer');
          setUserEmail(data?.email || emailForCreate);
          setUserPhone(data?.phone || phoneForCreate);
          setUserName(data?.name || data?.email || emailForCreate || 'Unknown');
          clearPendingAuth();

          if (result.created) {
            makeRedirect('customer');
          } else if (window.location.pathname === '/login') {
            makeRedirect(data?.role);
          }
        }
      } catch (error) {
        console.error('Error in ensureUserAndFetchRole:', error);
        setUserRole('customer');
      } finally {
        setRoleLoading(false);
      }
    } else {
      setUserRole('customer');
      // Keep pending OTP credentials across brief auth flickers; only clear on explicit sign-out
      setRoleLoading(false);
    }
  };

  // Ensure user exists in users table and fetch role
  useEffect(() => {
    ensureUserAndFetchRole();
  }, [user]);

  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      // If no user is found, we can set authLoading to false
      // Otherwise, we'll wait for the role to be fetched
      if (!session?.user) {
        setAuthLoading(false);
      }
    });

    // Listen for changes in auth state
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      // Only update if the session state actually changed
      setUser(prevUser => {
        const newUser = session?.user ?? null;
        if (JSON.stringify(prevUser) !== JSON.stringify(newUser)) {
          return newUser;
        }
        return prevUser;
      });
      
      if (!session?.user) {
        setAuthLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  // Set authLoading to false once we have both user and role
  useEffect(() => {
    if (user && userRole && roleLoading === false) {
      setAuthLoading(false);
    }
  }, [user, userRole, roleLoading]);

  // Compute overall loading state
  const loading = authLoading || roleLoading;

  // Sign out function
  const handleSignOut = async () => {
    clearPendingAuth();
    setUserEmail(null);
    setUserPhone(null);
    return await supabase.auth.signOut();
  };

  // Create a modified user object that includes email as userEmail and name as userName
  const modifiedUser = user ? {
    ...user,
    email: userEmail,
    name: userName
  } : null;

  const value = {
    signOut: handleSignOut,
    user: modifiedUser,
    userRole,
    userEmail,
    userPhone,
    isAdmin: userRole === 'admin' || userRole === 'owner',
    isOwner: userRole === 'owner',
    loading,
    authError,
    sendOtp,
    verifyOtp,
    formatPhoneNumber
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  return useContext(AuthContext);
};
